import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { PMPopUp } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { Configuration } from "product-test-types";
import { useMemo, useState } from "react";

import { ControlledTextFormField, FormFieldWrapper } from "./pimo-form";

export interface ColumnMapping {
  sourceColumn: string;
  targetField: string;
}

interface Props {
  /* Excel column titles */
  excelHeaders: string[];
  /* Item fields */
  itemFields: string[];
  open: boolean;
  onClose: () => void;
  fireEvent?: (eventName: string, data?: unknown) => void;
  isLoading?: boolean;
  excelData?: unknown[][];
  configuration?: Configuration;
  language?: string;
}

export const ExcelImportPopup: PimoReactComponent<Props> = ({
  excelHeaders,
  itemFields,
  open,
  onClose,
  fireEvent,
  isLoading,
  excelData = [],
  configuration,
  language = "en",
}) => {
  const getFieldLabel = (fieldKey: string): string => {
    const severityLabel =
      configuration?.fieldsDefinition?.find((f) => f.fieldName === "category")
        ?.label || "Severity";

    switch (fieldKey) {
      case "measure.name":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.measureName"
          ) || "Measure - Name"
        );
      case "measure.findings":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.measureFindings"
          ) || "Measure - Finding"
        );
      case "measure.description":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.measureDescription"
          ) || "Measure - Description"
        );
      case "measure.owner":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.measureOwner"
          ) || "Measure - Owner"
        );
      case "measure.startDate":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.measureStartDate"
          ) || "Measure - Start Date"
        );
      case "measure.endDate":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.measureEndDate"
          ) || "Measure - End Date"
        );
      case "measure.degreeOfCompletion":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.measureStatus"
          ) || "Measure - Status"
        );
      case "measure.dropdownFieldOne":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.measureTeam"
          ) || "Measure - Team"
        );
      case "finding.name":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.findingName"
          ) || "Finding - Name"
        );
      case "finding.description":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.findingDescription"
          ) || "Finding - Description"
        );
      case "finding.owner":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.findingOwner"
          ) || "Finding - Owner"
        );
      case "finding.budget":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.findingBudget"
          ) || "Finding - Budget"
        );
      case "finding.startDate":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.findingStartDate"
          ) || "Finding - Start Date"
        );
      case "finding.endDate":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.findingEndDate"
          ) || "Finding - End Date"
        );
      case "finding.severity":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.findingSeverity",
            { severityLabel }
          ) || `Finding - ${severityLabel}`
        );
      case "finding.freeTextFieldOne":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.findingAuditTitle"
          ) || "Finding - Audit Title"
        );
      case "finding.secondCategory":
        return (
          getLocalizedMessage(
            language,
            "excelImport.fieldLabels.findingAuditType"
          ) || "Finding - Audit Type"
        );
      default:
        return fieldKey;
    }
  };
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>(
    {}
  );
  const [headerRowIndex, setHeaderRowIndex] = useState<number>(0);
  const [dataStartRowIndex, setDataStartRowIndex] = useState<number>(1);

  // Derive headers from the selected header row instead of mirroring into state
  const currentHeaders = useMemo(() => {
    if (excelData.length > headerRowIndex && excelData[headerRowIndex]) {
      return (excelData[headerRowIndex] as unknown[])
        .map((cell, index) => String(cell || `Column ${index + 1}`))
        .filter((header) => header.trim().length > 0);
    }
    return excelHeaders;
  }, [headerRowIndex, excelData, excelHeaders]);

  // Reset column mapping during render when headers change.
  // Using the "store previous prop in state" pattern avoids
  // synchronously calling setState inside a useEffect.
  const [previousHeaders, setPreviousHeaders] =
    useState<string[]>(currentHeaders);
  if (previousHeaders !== currentHeaders) {
    setPreviousHeaders(currentHeaders);
    if (currentHeaders.length > 0) {
      const newMapping: Record<string, string> = {};
      currentHeaders.forEach((header) => {
        newMapping[header] = "";
      });
      setColumnMapping(newMapping);
    }
  }

  const handleSave = () => {
    const validMapping: Record<string, string> = {};
    Object.entries(columnMapping).forEach(([header, targetField]) => {
      if (targetField && targetField !== "") {
        if (
          targetField === "measure.degreeOfCompletion" ||
          targetField === "measure.findings" ||
          itemFields.includes(targetField)
        ) {
          validMapping[header] = targetField;
        }
      }
    });

    fireEvent?.("excel-import:save", {
      mapping: validMapping,
      headerRowIndex,
      dataStartRowIndex,
    });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2, overflow: "auto" }}>
        <Box sx={{ mb: 3, borderRadius: 1 }}>
          <Typography
            variant="body1"
            sx={{ mb: 2, fontWeight: 500, fontSize: 16 }}
          >
            {getLocalizedMessage(
              language,
              "excelImport.identifyHeaderAndDataRows"
            ) || "Identify Header and Data Rows"}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Box sx={{ width: "50%" }}>
              <FormFieldWrapper
                label={
                  getLocalizedMessage(
                    language,
                    "excelImport.rowWithColumnNames"
                  ) || "Row with column names"
                }
              >
                <ControlledTextFormField
                  isNumber
                  value={headerRowIndex.toString()}
                  onChange={(e) => {
                    setHeaderRowIndex(Math.max(0, parseInt(e ?? "1")));
                  }}
                  helperText={
                    getLocalizedMessage(
                      language,
                      "excelImport.rowWithColumnNamesHelper"
                    ) ||
                    "e.g. 1 (row that contains the column titles such as Name, Date…)"
                  }
                />
              </FormFieldWrapper>
            </Box>
            <Box sx={{ width: "50%" }}>
              <FormFieldWrapper
                label={
                  getLocalizedMessage(
                    language,
                    "excelImport.rowWhereRecordsStart"
                  ) || "Row where actual records start"
                }
              >
                <ControlledTextFormField
                  isNumber
                  value={dataStartRowIndex.toString()}
                  onChange={(e) =>
                    setDataStartRowIndex(Math.max(0, parseInt(e ?? "1")))
                  }
                  helperText={
                    getLocalizedMessage(
                      language,
                      "excelImport.rowWhereRecordsStartHelper"
                    ) || "e.g. 2 (first row that contains actual data)"
                  }
                />
              </FormFieldWrapper>
            </Box>
          </Box>

          {currentHeaders.length > 0 && (
            <Box>
              <Typography variant="caption">
                {getLocalizedMessage(
                  language,
                  "excelImport.currentHeadersFromRow",
                  { rowNumber: headerRowIndex + 1 }
                ) || `Current headers from row ${headerRowIndex + 1}:`}
              </Typography>
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "primary.700" }}
              >
                {currentHeaders.slice(0, 5).join(", ")}
                {currentHeaders.length > 5 &&
                  ` ... (+${currentHeaders.length - 5} more)`}
              </Typography>
            </Box>
          )}
          {/* 
          {excelData.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: 60 }}>
                      Row
                    </TableCell>
                    {excelData[0]?.map((_, colIndex) => (
                      <TableCell
                        key={colIndex}
                        sx={{ fontWeight: "bold", minWidth: 100 }}
                      >
                        Col {colIndex + 1}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {excelData.slice(0, 10).map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      sx={{
                        bgcolor:
                          rowIndex === headerRowIndex
                            ? "primary.50"
                            : rowIndex === dataStartRowIndex
                              ? "success.50"
                              : "inherit",
                        border:
                          rowIndex === headerRowIndex ? "2px solid" : "none",
                        borderColor:
                          rowIndex === headerRowIndex
                            ? "primary.300"
                            : "transparent",
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {rowIndex + 1}
                          {rowIndex === headerRowIndex && (
                            <Chip
                              label="Header Source"
                              size="small"
                              color="primary"
                            />
                          )}
                          {rowIndex === dataStartRowIndex && (
                            <Chip
                              label="Data Start"
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      </TableCell>
                      {(row as unknown[]).map((cell, colIndex) => (
                        <TableCell
                          key={colIndex}
                          sx={{
                            fontWeight:
                              rowIndex === headerRowIndex ? "bold" : "normal",
                            color:
                              rowIndex === headerRowIndex
                                ? "primary.800"
                                : "inherit",
                          }}
                        >
                          {String(cell || "").substring(0, 50)}
                          {String(cell || "").length > 50 && "..."}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )} */}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "400px 50px 400px",
              alignItems: "start",
              mb: 2,
            }}
          >
            <Typography variant="body1">
              {getLocalizedMessage(language, "excelImport.sourceExcelColumn") ||
                "Source (Excel Column)"}
            </Typography>
            <Box />
            <Typography variant="body1">
              {getLocalizedMessage(language, "excelImport.targetSystemField") ||
                "Target (System Field)"}
            </Typography>
          </Box>

          {currentHeaders.map((header, index) => (
            <Box
              key={index}
              sx={{
                display: "grid",
                gridTemplateColumns: "400px 50px 400px",
                alignItems: "center",
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <Typography sx={{ fontSize: 14 }}>{header}</Typography>
              <ArrowForwardIcon
                sx={{ margin: "0 16px", color: "primary.main" }}
              />
              <Select
                fullWidth
                value={columnMapping[header] || ""}
                onChange={(e) => {
                  setColumnMapping((prev) => ({
                    ...prev,
                    [header]: e.target.value,
                  }));
                }}
                size="small"
                sx={{
                  "& input": {
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },
                  fontSize: "14px",
                }}
              >
                <MenuItem value="">
                  <em>
                    {getLocalizedMessage(
                      language,
                      "excelImport.skipThisColumn"
                    ) || "Skip this column"}
                  </em>
                </MenuItem>
                {itemFields.map((field) => (
                  <MenuItem key={field} value={field} sx={{ fontSize: 14 }}>
                    {getFieldLabel(field)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <PMPopUp
      open={open}
      title={
        getLocalizedMessage(language, "excelImport.title") ||
        "Import Excel Data - Map Columns to Fields"
      }
      onClose={onClose}
      maxWidth="md"
      cancelButtonText={
        getLocalizedMessage(language, "excelImport.cancel") || "Cancel"
      }
      cancelButtonFireEvent={() => onClose()}
      confirmationButtonText={
        isLoading
          ? getLocalizedMessage(language, "excelImport.importing") ||
            "Importing..."
          : getLocalizedMessage(language, "excelImport.importData") ||
            "Import Data"
      }
      confirmationButtonFireEvent={() => handleSave()}
    >
      {renderContent()}
    </PMPopUp>
  );
};
